import type {
  CoachingGoal,
  CoachingGoalMilestone,
  CoachingGoalStatus,
} from '@efdigital/ll-addon-data';
import { milestonesForGoal } from '@efdigital/ll-xceed-v2/utils/coaching/goal-children';
import { formatGoalTargetDate } from '@efdigital/ll-xceed-v2/utils/coaching/goal-progress';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';

interface GoalCardSignature {
  Args: {
    goal: CoachingGoal;
    milestones: readonly CoachingGoalMilestone[];
    onOpen?: (goal: CoachingGoal) => void;
    route?: string;
    routeModels?: unknown[];
  };
  Element: HTMLLIElement;
}

const STATUS_LABEL: Record<CoachingGoalStatus, string> = {
  active: 'Active',
  achieved: 'Achieved',
  dropped: 'Dropped',
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status as CoachingGoalStatus] ?? status;
}

export default class GoalCard extends Component<GoalCardSignature> {
  @service declare router: RouterService;

  get milestones(): CoachingGoalMilestone[] {
    return milestonesForGoal(this.args.milestones, this.args.goal.id!);
  }

  get statusLabelText(): string {
    return statusLabel(this.args.goal.status ?? 'active');
  }

  get statusBadgeClass(): string {
    const status = this.args.goal.status ?? 'active';

    if (status === 'achieved') {
      return 'badge rounded-pill text-uppercase text-bg-success';
    }

    if (status === 'dropped') {
      return 'badge rounded-pill text-uppercase text-bg-secondary';
    }

    return 'badge rounded-pill text-uppercase text-bg-primary';
  }

  get statusIcon(): string {
    const status = this.args.goal.status ?? 'active';

    if (status === 'achieved') {
      return 'ph:check-circle-fill';
    }

    if (status === 'dropped') {
      return 'ph:minus-circle';
    }

    return 'ph:clock';
  }

  get targetDateValue(): string {
    return formatGoalTargetDate(this.args.goal.targetDate) ?? 'Not set';
  }

  get completedCount(): number {
    return this.milestones.filter((milestone) => milestone.completed).length;
  }

  get milestoneValue(): string {
    const { milestones, completedCount } = this;

    if (milestones.length === 0) {
      return 'None';
    }

    return `${completedCount}/${milestones.length}`;
  }

  get milestoneValueClass(): string {
    if (!this.hasMilestones) {
      return 'text-body-secondary';
    }

    return this.completedCount > 0 ? 'fw-semibold' : '';
  }

  get hasMilestones(): boolean {
    return this.milestones.length > 0;
  }

  open = () => {
    const { route, routeModels = [], onOpen, goal } = this.args;

    if (route && goal.id) {
      void this.router.transitionTo(route, ...routeModels, goal.id);
      return;
    }

    onOpen?.(goal);
  };

  <template>
    <li
      class="card border shadow-sm rounded-4 h-100 d-flex flex-column overflow-hidden"
      data-test-goal-card
      ...attributes
    >
      <div class="card-body p-4 flex-grow-1">
        <div
          class="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-3"
        >
          <div class="flex-grow-1 min-w-0 overflow-hidden ps-2">
            <span
              class="{{this.statusBadgeClass}}
                d-inline-flex align-items-center gap-1"
            >
              <iconify-icon
                inline
                icon={{this.statusIcon}}
                aria-hidden="true"
              />
              {{this.statusLabelText}}
            </span>
            <h3 class="h4 fw-bold mt-2 mb-0 text-break">{{@goal.title}}</h3>
          </div>
          <button
            type="button"
            class="btn btn-outline-primary rounded-pill flex-shrink-0 text-nowrap align-self-stretch align-self-sm-start"
            {{on "click" this.open}}
          >
            Open
            <span class="visually-hidden"> {{@goal.title}}</span>
            <iconify-icon inline icon="ph:caret-right" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        class="card-footer bg-transparent border-top d-flex align-items-center p-4 mt-auto"
      >
        <div
          class="d-flex flex-column flex-sm-row align-items-start gap-3 column-gap-sm-5 ps-2 min-w-0"
        >
          <div class="flex-shrink-0">
            <p class="gud-eyebrow text-body-secondary mb-1">Target date</p>
            <p class="mb-0 text-nowrap">{{this.targetDateValue}}</p>
          </div>
          <div class="min-w-0">
            <p class="gud-eyebrow text-body-secondary mb-1">Milestones</p>
            <p class="mb-0 {{this.milestoneValueClass}}">
              {{this.milestoneValue}}
            </p>
          </div>
        </div>
      </div>
    </li>
  </template>
}
